import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useMemo } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { MARKER_TYPES, FILTER_KEYS, isHeartType } from '../constants';
const STORAGE_KEY = 'explore_filters';
const COMMUNITY_ID_PATTERN = /^community-(\d+)$/;
function buildCommunityOptions(markers) {
  return markers.filter(item => item.type === MARKER_TYPES.COMMUNITY).map(item => ({
    value: item.id,
    label: item.name
  }));
}
function markerBelongsToCommunity(marker, selectedCommunities) {
  const selectedIds = selectedCommunities.map(id => {
    const match = id.match(COMMUNITY_ID_PATTERN);
    return match ? parseInt(match[1], 10) : null;
  }).filter(Boolean);
  if ((marker.type === MARKER_TYPES.EVENT || marker.type === MARKER_TYPES.PROMOTION) && marker.communityIds) {
    return marker.communityIds.some(cid => selectedIds.includes(cid));
  }
  if ((marker.type === MARKER_TYPES.ATTRACTION || isHeartType(marker.type)) && marker.communityId) {
    return selectedIds.includes(marker.communityId);
  }
  return false;
}

/**
 * Save filters to sessionStorage
 * @param {Object} filters - Filter state to save
 */
function saveFiltersToSession(filters) {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch (error) {
    console.error('Failed to save filters to sessionStorage:', error);
  }
}

/**
 * Build URL params from current filter state
 * Only includes params that differ from default (all ON) state
 */
function buildUrlParamsFromFilters(filters, initialMarkers, searchQuery, searchCommitted) {
  const params = new URLSearchParams();

  // Check if filtering by specific community (one or more)
  if (!filters.communities?.all && (filters.communities?.selected?.length || 0) > 0) {
    const selectedIds = filters.communities.selected || [];
    const communityTokens = selectedIds.map(id => {
      const community = initialMarkers.find(m => m.id === id && m.type === 'community');
      return community?.slug || community?.id || null;
    }).filter(Boolean);
    if (communityTokens.length > 0) {
      params.set('community', communityTokens.join(','));
    }
  }

  // Build types param for entity type filters that are OFF
  const activeTypes = [];
  if (filters.hearts) activeTypes.push('hearts');
  if (filters.artists) activeTypes.push('artists');
  if (filters.events) activeTypes.push('events');
  const hasCommunities = filters.communities?.all || (filters.communities?.selected?.length || 0) > 0;
  if (hasCommunities) activeTypes.push('communities');
  const hasAttractions = filters.attractions?.all || (filters.attractions?.selected?.length || 0) > 0;
  if (hasAttractions) activeTypes.push('attractions');
  const promotionKeys = ['dining', 'attractions', 'sports', 'arts'];
  const hasPromotions = filters.promotions?.all || promotionKeys.some(key => filters.promotions?.[key] !== false);
  if (hasPromotions) activeTypes.push('promotions');

  // Add attraction selections when not all are active
  if (hasAttractions && !filters.attractions?.all) {
    const selectedAttractionIds = filters.attractions?.selected || [];
    if (selectedAttractionIds.length > 0) {
      params.set('attr', selectedAttractionIds.join(','));
    }
  }

  // Add promotion sub-filters when not all categories are active
  if (hasPromotions) {
    const activePromoKeys = promotionKeys.filter(key => filters.promotions?.[key] !== false);
    if (activePromoKeys.length > 0 && activePromoKeys.length < promotionKeys.length) {
      params.set('promo', activePromoKeys.join(','));
    }
  }

  // Only add types param if not all types are active (some are filtered out)
  const allTypesActive = activeTypes.length === 6;
  if (!allTypesActive || params.has('promo') || params.has('attr')) {
    params.set('types', activeTypes.length > 0 ? activeTypes.join(',') : 'none');
  }
  const trimmedSearch = searchQuery?.trim();
  if (searchCommitted && trimmedSearch) {
    params.set('search', trimmedSearch);
  }
  return params;
}
export function useExploreFilters(state, dispatch, initialMarkers, t0) {
  const $ = _c(50);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    isHydrated: t2,
    suppressUrlSyncRef,
    attractionCategories: t3
  } = t1;
  const isHydrated = t2 === undefined ? false : t2;
  const attractionCategories = t3 === undefined ? null : t3;
  const router = useRouter();
  const pathname = usePathname();
  const isInitialMount = useRef(true);
  const lastSavedFilters = useRef(null);
  let t4;
  let t5;
  if ($[2] !== isHydrated || $[3] !== state.filters) {
    t4 = () => {
      if (!isHydrated) {
        return;
      }
      const filtersJson = JSON.stringify(state.filters);
      if (lastSavedFilters.current === filtersJson) {
        return;
      }
      lastSavedFilters.current = filtersJson;
      saveFiltersToSession(state.filters);
    };
    t5 = [state.filters, isHydrated];
    $[2] = isHydrated;
    $[3] = state.filters;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  const lastUrlRef = useRef("");
  let t6;
  if ($[6] !== initialMarkers || $[7] !== isHydrated || $[8] !== pathname || $[9] !== router || $[10] !== state.filters || $[11] !== state.searchCommitted || $[12] !== state.searchQuery || $[13] !== state.viewMode || $[14] !== suppressUrlSyncRef) {
    t6 = () => {
      if (!isHydrated) {
        return;
      }
      if (initialMarkers.length === 0) {
        return;
      }
      if (isInitialMount.current) {
        isInitialMount.current = false;
        return;
      }
      if (suppressUrlSyncRef?.current) {
        suppressUrlSyncRef.current = false;
        return;
      }
      const filterParams = buildUrlParamsFromFilters(state.filters, initialMarkers, state.searchQuery, state.searchCommitted);
      const currentParams = new URLSearchParams(window.location.search);
      const viewParam = currentParams.get("view");
      if (state.viewMode === "map") {
        filterParams.set("view", "map");
      } else {
        if (viewParam) {
          filterParams.set("view", viewParam);
        }
      }
      const newUrl = filterParams.toString() ? `${pathname}?${filterParams.toString()}` : pathname;
      if (newUrl !== lastUrlRef.current) {
        lastUrlRef.current = newUrl;
        router.replace(newUrl, {
          scroll: false
        });
      }
    };
    $[6] = initialMarkers;
    $[7] = isHydrated;
    $[8] = pathname;
    $[9] = router;
    $[10] = state.filters;
    $[11] = state.searchCommitted;
    $[12] = state.searchQuery;
    $[13] = state.viewMode;
    $[14] = suppressUrlSyncRef;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== initialMarkers || $[17] !== isHydrated || $[18] !== pathname || $[19] !== router || $[20] !== state.filters || $[21] !== state.searchCommitted || $[22] !== state.searchQuery || $[23] !== suppressUrlSyncRef) {
    t7 = [state.filters, state.searchQuery, state.searchCommitted, isHydrated, initialMarkers, pathname, router, suppressUrlSyncRef];
    $[16] = initialMarkers;
    $[17] = isHydrated;
    $[18] = pathname;
    $[19] = router;
    $[20] = state.filters;
    $[21] = state.searchCommitted;
    $[22] = state.searchQuery;
    $[23] = suppressUrlSyncRef;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  useEffect(t6, t7);
  let t8;
  if ($[25] !== initialMarkers) {
    t8 = buildCommunityOptions(initialMarkers);
    $[25] = initialMarkers;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const communityOptions = t8;
  let t9;
  if ($[27] !== attractionCategories?.filterOptions) {
    t9 = attractionCategories?.filterOptions || [];
    $[27] = attractionCategories?.filterOptions;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const attractionCategoryOptions = t9;
  let t10;
  if ($[29] !== communityOptions) {
    t10 = () => communityOptions;
    $[29] = communityOptions;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const getUniqueCommunities = t10;
  let t11;
  if ($[31] !== attractionCategoryOptions) {
    t11 = () => attractionCategoryOptions;
    $[31] = attractionCategoryOptions;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  const getUniqueAttractionCategories = t11;
  const filterMarkers = _temp;
  let t12;
  if ($[33] !== dispatch) {
    t12 = newFilters => {
      dispatch({
        type: "SET_FILTERS",
        payload: newFilters
      });
    };
    $[33] = dispatch;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const setFilters = t12;
  let t13;
  if ($[35] !== getUniqueAttractionCategories || $[36] !== getUniqueCommunities || $[37] !== state.filters) {
    t13 = () => {
      let count = 0;
      if (!state.filters.hearts) {
        count++;
      }
      if (!state.filters.artists) {
        count++;
      }
      if (!state.filters.events) {
        count++;
      }
      if (!state.filters.communities.all) {
        const allCommunities = getUniqueCommunities();
        const totalCommunities = allCommunities.length;
        const selectedCount = state.filters.communities.selected.length;
        const excludedCount = totalCommunities - selectedCount;
        count = count + excludedCount;
      }
      if (!state.filters.attractions.all) {
        const allAttractions = getUniqueAttractionCategories();
        const totalAttractions = allAttractions.length;
        const selectedCount_0 = state.filters.attractions.selected.length;
        const excludedCount_0 = totalAttractions - selectedCount_0;
        count = count + excludedCount_0;
      }
      if (!state.filters.promotions.all) {
        count++;
      } else {
        if (!state.filters.promotions.dining) {
          count++;
        }
        if (!state.filters.promotions.attractions) {
          count++;
        }
        if (!state.filters.promotions.sports) {
          count++;
        }
        if (!state.filters.promotions.arts) {
          count++;
        }
      }
      return count;
    };
    $[35] = getUniqueAttractionCategories;
    $[36] = getUniqueCommunities;
    $[37] = state.filters;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  const getActiveFilterCount = t13;
  let t14;
  if ($[39] !== state.searchCommitted || $[40] !== state.searchQuery) {
    t14 = (filters_0, markers_0) => buildUrlParamsFromFilters(filters_0, markers_0, state.searchQuery, state.searchCommitted);
    $[39] = state.searchCommitted;
    $[40] = state.searchQuery;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  let t15;
  if ($[42] !== attractionCategoryOptions || $[43] !== getActiveFilterCount || $[44] !== getUniqueAttractionCategories || $[45] !== getUniqueCommunities || $[46] !== setFilters || $[47] !== state.filters || $[48] !== t14) {
    t15 = {
      filters: state.filters,
      setFilters,
      filterMarkers,
      getUniqueCommunities,
      getUniqueAttractionCategories,
      getActiveFilterCount,
      attractionCategories: attractionCategoryOptions,
      buildUrlParamsFromFilters: t14
    };
    $[42] = attractionCategoryOptions;
    $[43] = getActiveFilterCount;
    $[44] = getUniqueAttractionCategories;
    $[45] = getUniqueCommunities;
    $[46] = setFilters;
    $[47] = state.filters;
    $[48] = t14;
    $[49] = t15;
  } else {
    t15 = $[49];
  }
  return t15;
}
function _temp(markers, filters, t0, t1) {
  const viewMode = t0 === undefined ? null : t0;
  const isSearchContext = t1 === undefined ? false : t1;
  const isCommunityFiltered = !filters[FILTER_KEYS.COMMUNITIES].all && filters[FILTER_KEYS.COMMUNITIES].selected.length > 0;
  return markers.filter(marker => {
    if (isHeartType(marker.type)) {
      if (!filters[FILTER_KEYS.HEARTS]) {
        return false;
      }
      if (isCommunityFiltered) {
        return markerBelongsToCommunity(marker, filters[FILTER_KEYS.COMMUNITIES].selected);
      }
      return true;
    }
    if (marker.type === MARKER_TYPES.ARTIST) {
      if (!filters[FILTER_KEYS.ARTISTS]) {
        return false;
      }
      const isMapView = viewMode === "map";
      if (filters[FILTER_KEYS.HEARTS] && isMapView && !isSearchContext) {
        return false;
      }
      if (isCommunityFiltered && marker.communityId) {
        return markerBelongsToCommunity({
          ...marker,
          type: MARKER_TYPES.HEART
        }, filters[FILTER_KEYS.COMMUNITIES].selected);
      }
      return true;
    }
    if (marker.type === MARKER_TYPES.EVENT) {
      if (!filters[FILTER_KEYS.EVENTS]) {
        return false;
      }
      if (isCommunityFiltered) {
        return markerBelongsToCommunity(marker, filters[FILTER_KEYS.COMMUNITIES].selected);
      }
      return true;
    }
    if (marker.type === MARKER_TYPES.COMMUNITY) {
      if (filters[FILTER_KEYS.COMMUNITIES].all) {
        return true;
      }
      return filters[FILTER_KEYS.COMMUNITIES].selected.includes(marker.id);
    }
    if (marker.type === MARKER_TYPES.ATTRACTION) {
      if (!filters[FILTER_KEYS.ATTRACTIONS].all) {
        const markerCategoryIds = Array.isArray(marker.categoryIds) ? marker.categoryIds.map(String) : [];
        const hasMatchingCategory = markerCategoryIds.some(categoryId => filters[FILTER_KEYS.ATTRACTIONS].selected.includes(categoryId));
        if (!hasMatchingCategory) {
          return false;
        }
      }
      if (isCommunityFiltered) {
        return markerBelongsToCommunity(marker, filters[FILTER_KEYS.COMMUNITIES].selected);
      }
      return true;
    }
    if (marker.type === MARKER_TYPES.PROMOTION) {
      let passesCategory;
      if (marker.category === "dining") {
        passesCategory = filters[FILTER_KEYS.PROMOTIONS].dining !== false;
      } else {
        if (marker.category === "attractions") {
          passesCategory = filters[FILTER_KEYS.PROMOTIONS].attractions !== false;
        } else {
          if (marker.category === "sports") {
            passesCategory = filters[FILTER_KEYS.PROMOTIONS].sports !== false;
          } else {
            if (marker.category === "arts") {
              passesCategory = filters[FILTER_KEYS.PROMOTIONS].arts !== false;
            } else {
              const allCategoriesOff = filters[FILTER_KEYS.PROMOTIONS].dining === false && filters[FILTER_KEYS.PROMOTIONS].attractions === false && filters[FILTER_KEYS.PROMOTIONS].sports === false && filters[FILTER_KEYS.PROMOTIONS].arts === false;
              passesCategory = !allCategoriesOff;
            }
          }
        }
      }
      if (!passesCategory) {
        return false;
      }
      if (isCommunityFiltered) {
        return markerBelongsToCommunity(marker, filters[FILTER_KEYS.COMMUNITIES].selected);
      }
      return true;
    }
    return true;
  });
}